import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { PhysicsStack } from "../physics/physics_masses.js";
import { BlockMassTuner } from "../debug/block_mass_tuner.js";

export interface BreakablePhysicsConfig
{
  arm_delay: number;
  angular_damping: number;
  collider_desc?: RAPIER.ColliderDesc;
  friction: number;
  linear_damping: number;
  angular_rest_threshold_sq?: number;
  linear_rest_threshold_sq?: number;
  mass: number;
  restitution: number;

  initial_rotation_x?: number;
  initial_rotation_y?: number;
  initial_rotation_z?: number;

  extra_colliders?: { desc: RAPIER.ColliderDesc; mass_share: number }[];
}

export abstract class BreakablePhysicsEntity
{
  public readonly mesh: THREE.Object3D;
  public readonly body: RAPIER.RigidBody;
  public readonly collider: RAPIER.Collider;
  public readonly size: THREE.Vector3;

  protected destroying = false;

  private alive = true;
  private age = 0;
  private readonly arm_delay: number;
  private readonly angular_rest_threshold_sq: number;
  private readonly linear_rest_threshold_sq: number;
  private impact_speed = 0;
  private readonly previous_velocity = new THREE.Vector3();
  private readonly extra_colliders: RAPIER.Collider[] = [];
  private disposed = false;

  protected constructor(
    mesh: THREE.Object3D,
    position: THREE.Vector3,
    config: BreakablePhysicsConfig
  )
  {
    this.arm_delay = config.arm_delay;
    this.angular_rest_threshold_sq = config.angular_rest_threshold_sq ?? 0.05;
    this.linear_rest_threshold_sq = config.linear_rest_threshold_sq ?? 0.02;
    this.mesh = mesh;
    this.mesh.renderOrder = 10;

    this.size = new THREE.Box3()
      .setFromObject( this.mesh )
      .getSize( new THREE.Vector3() );
    if (
      !Number.isFinite( this.size.x ) ||
      !Number.isFinite( this.size.y ) ||
      !Number.isFinite( this.size.z ) ||
      this.size.x <= 0 ||
      this.size.y <= 0 ||
      this.size.z <= 0
    )
      throw new Error( `${this.constructor.name} mesh has invalid dimensions` );

    SceneManager.world.add( this.mesh );

    const body_desc = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation( position.x, position.y, position.z )
      .setLinearDamping( config.linear_damping )
      .setAngularDamping( config.angular_damping );

    if ( config.initial_rotation_x || config.initial_rotation_y || config.initial_rotation_z )
    {
      const euler = new THREE.Euler(
        config.initial_rotation_x ?? 0,
        config.initial_rotation_y ?? 0,
        config.initial_rotation_z ?? 0,
        "XYZ"
      );
      const quat = new THREE.Quaternion().setFromEuler( euler );
      body_desc.setRotation( { x: quat.x, y: quat.y, z: quat.z, w: quat.w } );
    }

    this.body = PhysicsWorld.getWorld().createRigidBody( body_desc );
    this.body.enableCcd( true );
    this.body.setAdditionalSolverIterations( PhysicsStack.additional_solver_iterations );

    const collider_desc = config.collider_desc ?? RAPIER.ColliderDesc.cuboid(
        this.size.x * 0.5,
        this.size.y * 0.5,
        this.size.z * 0.5
      );
    this.collider = PhysicsWorld.getWorld().createCollider(
      collider_desc
        .setFriction( config.friction )
        .setRestitution( config.restitution ),
      this.body
    );

    const extras = config.extra_colliders ?? [];
    const total_mass = BlockMassTuner.applyMass( config.mass );
    const extra_share = extras.reduce( ( sum, extra ) => sum + extra.mass_share, 0 );

    this.collider.setMass( total_mass * ( 1 - extra_share ) );

    for ( const extra of extras )
    {
      const collider = PhysicsWorld.getWorld().createCollider(
        extra.desc
          .setFriction( config.friction )
          .setRestitution( config.restitution ),
        this.body
      );

      collider.setMass( total_mass * extra.mass_share );
      this.extra_colliders.push( collider );
    }
  }

  protected halfHeight(): number
  {
    const q = this.body.rotation();
    const from_x = 2 * ( q.x * q.y + q.w * q.z );
    const from_y = 1 - 2 * ( q.x * q.x + q.z * q.z );
    const from_z = 2 * ( q.y * q.z - q.w * q.x );

    return ( Math.abs( from_x ) * this.size.x + Math.abs( from_y ) * this.size.y + Math.abs( from_z ) * this.size.z ) * 0.5;
  }

  public hasExtraColliders(): boolean
  {
    return this.extra_colliders.length > 0;
  }

  public removeExtraColliders(): void
  {
    for ( const collider of this.extra_colliders )
      PhysicsWorld.getWorld().removeCollider( collider, true );

    this.extra_colliders.length = 0;
  }

  public isAlive(): boolean
  {
    return this.alive;
  }

  public isAtRest(): boolean
  {
    if ( this.destroying ) return false;
    if ( this.body.isSleeping() ) return true;

    const linear_velocity = this.body.linvel();
    const angular_velocity = this.body.angvel();
    return linear_velocity.x ** 2 + linear_velocity.y ** 2 + linear_velocity.z ** 2 <
      this.linear_rest_threshold_sq &&
      angular_velocity.x ** 2 + angular_velocity.y ** 2 + angular_velocity.z ** 2 <
      this.angular_rest_threshold_sq;
  }

  public getImpactSpeed(): number
  {
    return this.impact_speed;
  }

  public isArmed(): boolean
  {
    return this.age >= this.arm_delay;
  }

  public getPosition(): THREE.Vector3
  {
    const position = this.body.translation();
    return new THREE.Vector3( position.x, position.y, position.z );
  }

  public update( dt: number ): void
  {
    if ( this.destroying )
    {
      this.updateDestroying( dt );
      this.syncTransform();
      return;
    }

    this.age += dt;

    const velocity = this.body.linvel();

    const dvx = velocity.x - this.previous_velocity.x;
    const dvy = velocity.y - this.previous_velocity.y;
    const dvz = velocity.z - this.previous_velocity.z;
    this.impact_speed = Math.sqrt( dvx * dvx + dvy * dvy + dvz * dvz );
    this.previous_velocity.set( velocity.x, velocity.y, velocity.z );

    this.syncTransform();
  }

  protected resetArmDelay(): void
  {
    this.age = 0;
  }

  protected markDestroyed(): boolean
  {
    if ( this.destroying ) return false;

    this.destroying = true;
    this.alive = false;
    return true;
  }

  protected beginDestroying(): boolean
  {
    if ( this.destroying ) return false;
    this.destroying = true;
    return true;
  }

  protected markDead(): void
  {
    this.alive = false;
    this.dead = true;
  }

  protected dead = false;

  protected updateDestroying( _dt: number ): void {}

  private syncTransform(): void
  {
    const position = this.body.translation();
    const rotation = this.body.rotation();
    this.mesh.position.set( position.x, position.y, position.z );
    this.mesh.quaternion.set( rotation.x, rotation.y, rotation.z, rotation.w );
  }

  public dispose(): void
  {
    if ( this.disposed ) return;
    this.disposed = true;

    SceneManager.world.remove( this.mesh );
    try { PhysicsWorld.getWorld().removeRigidBody( this.body ); } catch {}
  }
}
